import {
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { InfoOutlined, PersonOutline, Search } from '@mui/icons-material';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { getColor } from '../../../../../assets/theme';
import InputFieldsController from '../../../../../components/form/controller/input-fields.controller';
import { taxCreditSchema } from '../../../interface/room-posting-schema-interface';

import LoadingComponent from '../../../../../components/animation/loading-component';
import useToast from '../../../../../hooks/useToast';
import { LoadingButton } from '@mui/lab';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { usePostCWTransactionMutation } from '../../../../../api/cas/cas.api.rtk';
import AutoCompleteController from '../../../../../components/form/controller/autocomplete.controller';
import CustomNumberFormat from '../../../../../components/form/custom-number-format';
import {
  CWTPostTransaction,
  TaxCodeListIE,
} from '../../../../../interfaces/cas.interface';
import { formatNumber } from '../../../../../utils/format';
import FoGuestListTable from '../fo-guest-list-table';

import { RootState, useAppSelector } from '../../../../../store';
import { selectFOSalesDate } from '@store/selector/app.selector';

export interface FormValues {
  net_amount: number;
  credit_amount: number;
  payor_name: string;
  payor_tin: string;
  tax_code: string;
}

const FORoomPostingTaxCreditForm = ({
  handleClose,
  selectedFolioUuid,
  selectedSubGroup,
  ledgerRefetch,
}: {
  handleClose: () => void;
  selectedFolioUuid: string;
  selectedSubGroup: number;
  ledgerRefetch: () => void;
}) => {
  const { primary, white, success } = getColor();
  const { toast } = useToast();

  const currentSalesDate = useAppSelector(selectFOSalesDate);

  const [vatTaxType, setVatTaxType] = useState('CWT'); //CWT, CVAT

  const [profileId, setProfileId] = useState<string>('');
  const [profileName, setProfileName] = useState<string>('');
  const [profileTIN, setProfileNameTin] = useState<string>('');

  const [percentage, setPercentage] = useState<number>(0.0);
  const [taxCodeID, setTaxCodeID] = useState<number>(0);
  const [userAmountSel, setUserAmountSel] = useState<string>('');
  const [taxCodeList, setTaxCodeList] = useState<TaxCodeListIE[]>([]);
  const [optionTaxCode, setOptionTaxCode] = useState<string[]>([]);

  const selectedPropertyID = useSelector(
    (state: RootState) => state.properties.selectedProperty.value,
  );

  const methods = useForm<FormValues>({
    defaultValues: {
      net_amount: 0,
      credit_amount: 0,
      payor_name: '',
      payor_tin: '',
      tax_code: '',
    },
    resolver: yupResolver(taxCreditSchema),

    mode: 'onChange',
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
  } = methods;

  const [isGuestOpen, setIsGuestOpen] = useState(false);

  const handleOpenGuestList = () => {
    setIsGuestOpen(true);
  };

  const handleCloseGuestList = () => {
    setIsGuestOpen(false);
  };

  useEffect(() => {
    if (!watch('credit_amount')) return;

    {
      const creditAmount = watch('credit_amount');
      const netAmount = creditAmount / percentage;
      setValue('net_amount', netAmount);
      setUserAmountSel('Credit Amount');
    }
  }, [watch('credit_amount')]);

  useEffect(() => {
    if (!watch('net_amount')) return;
    {
      const netAmount = watch('net_amount');
      const creditAmount = netAmount * percentage;
      setValue('credit_amount', creditAmount);
      setUserAmountSel('Net Amount');
    }
  }, [watch('net_amount')]);

  const handleTaxChange = (code: string) => {
    try {
      const selectedPercent = taxCodeList?.find(
        (codelist) => codelist?.code === code,
      );
      if (selectedPercent) {
        if (selectedPercent.taxRate > 0) {
          setPercentage(selectedPercent.taxRate / 100);
          setTaxCodeID(selectedPercent.id);
        }
      } else {
        setPercentage(0);
        setTaxCodeID(0);
      }

      setValue('credit_amount', 0);
      setValue('net_amount', 0);
    } catch (error) {
      console.log('Error Select tax: ' + code);
    }
  };

  //#region useEffect

  const taxCodeData = useAppSelector((state) => state.accgTaxCode);

  useEffect(() => {
    // Check if TaxCodeData is defined
    if (taxCodeData?.dataResult) {
      // Find the taxCodeDataType based on vatType
      const taxCodeDataType = taxCodeData?.dataResult.filter(
        (item) => item.vatType === vatTaxType,
      );

      if (taxCodeDataType && taxCodeDataType.length > 0) {
        const taxCodeListTransformed: TaxCodeListIE[] = taxCodeDataType.map(
          (item) =>
            ({
              id: item.id,
              code: item.code,
              isActive: item.isActive,
              taxRate: Number(item.taxRate),
              taxType: item.taxType,
              vatType: item.vatType,
              description: item.description,
            }) as TaxCodeListIE,
        );

        setTaxCodeList(taxCodeListTransformed);
      } else {
        // If no matching data, clear the list
        setTaxCodeList([]);
      }
    }
  }, [taxCodeData, vatTaxType]);

  useEffect(() => {
    if (taxCodeList) {
      const mappedOptions = (taxCodeList || []).map(
        (option) => option?.code || '',
      );

      setOptionTaxCode(mappedOptions);
    }
  }, [taxCodeList]);

  // API Posting Entry
  const [postCWTTransactionAPI, { isLoading: isFetching }] =
    usePostCWTransactionMutation();

  const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
    const transactionIdentifier = selectedFolioUuid;
    const subGroupId = selectedSubGroup;
    const cutOffDate = moment(currentSalesDate).format('YYYY-MM-DD');

    const lineRemarks = `Apply CWT for ${data.payor_tin} - ${data.payor_name} Tax Code(${data.tax_code} )`;

    const jsonBody: CWTPostTransaction = {
      properties: [
        {
          property: {
            propertyID: selectedPropertyID,
            cwt_transaction: [
              {
                transaction_id: transactionIdentifier,
                cutoff_date: cutOffDate,
                module_id: 1,
                sub_group_id: subGroupId,
                remarks: lineRemarks,
                payors_id: profileId,
                tax_type: vatTaxType,
                tax_id: taxCodeID,
                input_amount_type: userAmountSel,
                credit: data.credit_amount,
                net: data.net_amount,
              },
            ],
          },
        },
      ],
    };

    try {
      await postCWTTransactionAPI(jsonBody).unwrap();

      toast({ severity: 'success', message: 'Successfully posted CWT.' });
      ledgerRefetch();
      handleClose();
    } catch (err) {
      toast({
        severity: 'error',
        message: err,
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <Stack sx={{ minWidth: '370px' }}>
        {isFetching && <LoadingComponent />}
        <Stack component='form' onSubmit={handleSubmit(onSubmit)}>
          <Stack
            sx={{
              p: 3,
              borderBottom: '1px dashed lightgray',
            }}
            gap='5px'
            width='100%'
          >
            <FormControl sx={{ width: '100%' }} className='form-control'>
              <InputFieldsController
                label='Payor`s name'
                name='payor_name'
                value={profileName}
                control={control}
                errors={errors}
                inputStyle={{
                  '.MuiInputBase-root': { width: '100%' },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <PersonOutline />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        edge='end'
                        onClick={(e) => {
                          e.preventDefault();
                          handleOpenGuestList();
                        }}
                        style={{
                          padding: '6px',
                          backgroundColor: primary,
                          borderRadius: '4px',
                          color: white,
                        }}
                      >
                        <Search />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                borderErrorOnly
              />

              <InputFieldsController
                label='Payor`s Tax Identification Number'
                name='payor_tin'
                control={control}
                errors={errors}
                wrapperStyle={{
                  minWidth: '150px',
                  width: '100%',
                  marginBottom: 0,
                }}
                //  placeholder='000-000-000-000'
                borderErrorOnly
                value={profileTIN}
              />

              <Typography sx={{ flexGrow: 1, p: '5px', mt: '5px' }}>
                Select Tax Credit Type
              </Typography>
              <Stack spacing={1} direction='row' sx={{ overflow: 'auto' }}>
                <Chip
                  label={`CWT`}
                  clickable
                  onClick={() => {
                    setVatTaxType('CWT');
                    setValue('tax_code', '');
                    setValue('credit_amount', 0);
                    setValue('net_amount', 0);
                    setPercentage(0);
                  }}
                  sx={{
                    backgroundColor: white,
                    color: vatTaxType !== 'CWT' ? '#000000' : success,
                    borderColor: vatTaxType !== 'CWT' ? '#bdbdbd' : success,
                    width: '80px',
                    height: '32px',
                    borderRadius: '100px',
                    padding: '4px',
                  }}
                  variant='outlined'
                />
                <Chip
                  label={`CVAT`}
                  clickable
                  onClick={() => {
                    setVatTaxType('CVAT');
                    setValue('tax_code', '');
                    setPercentage(0);
                    setValue('credit_amount', 0);
                    setValue('net_amount', 0);
                  }}
                  sx={{
                    backgroundColor: white,
                    color: vatTaxType !== 'CVAT' ? '#000000' : success,
                    borderColor: vatTaxType !== 'CVAT' ? '#bdbdbd' : success,
                    width: '80px',
                    height: '32px',
                    borderRadius: '100px',
                    padding: '4px',
                  }}
                  variant='outlined'
                />
              </Stack>

              <Typography sx={{ flexGrow: 1, p: '5px', mt: '5px' }}>
                Select Tax Code
              </Typography>

              <AutoCompleteController
                label='Tax Code'
                name='tax_code'
                options={optionTaxCode.sort() || []}
                getOptionLabel={(option) => option || ''}
                control={control}
                errors={errors}
                textFieldProps={{
                  sx: {
                    width: { xs: '100%', lg: '100%' },
                  },
                  size: 'small',
                }}
                onChange={(value: string) => handleTaxChange(value)}
              />
              <Typography
                sx={[
                  {
                    mt: '-4px',
                    pl: '10px',
                    fontSize: '11px!important',
                    fontWeight: 600,
                    letterSpacing: '.15px',
                    lineHeight: '160%',
                    color: '#00b8d9',
                    backgroundColor: '#e5f6fd',
                  },
                ]}
                variant='h6'
              >
                <Tooltip
                  title={`Witholding Tax Percentage`}
                  placement='right-end'
                >
                  <IconButton
                    sx={{
                      border: 0,
                      color: '#00b8d9',
                      bgcolor: 'transparent',
                      ':hover': {
                        color: primary,
                        bgcolor: 'transparent',
                      },
                    }}
                    disableRipple
                  >
                    <InfoOutlined />
                  </IconButton>
                </Tooltip>
                {`  ${formatNumber(percentage * 100, 2, 2)} %`}
              </Typography>
              <Typography sx={{ flexGrow: 1, p: '5px', mt: '5px' }}>
                Input Tax Withheld
              </Typography>

              <InputFieldsController
                label='Credit Amount'
                name='credit_amount'
                control={control}
                errors={errors}
                inputProps={{
                  maxLength: 8,
                  style: { textAlign: 'right' },
                }}
                InputProps={{
                  inputComponent: CustomNumberFormat,
                }}
              />

              <InputFieldsController
                label='Net Amount'
                name='net_amount'
                control={control}
                errors={errors}
                inputProps={{
                  maxLength: 8,
                  style: { textAlign: 'right' },
                }}
                InputProps={{
                  inputComponent: CustomNumberFormat,
                }}
                wrapperStyle={{ mb: 0 }}
              />
            </FormControl>
          </Stack>

          {/* BUTTON */}
          <Stack alignItems='flex-end' p={3}>
            <Stack direction='row' gap='5px'>
              <Button
                sx={{
                  width: '75px',
                  height: '35px',
                  border: '1px solid',
                  backgroundColor: 'transparent',
                }}
                onClick={handleClose}
              >
                Cancel
              </Button>
              <LoadingButton
                type='submit'
                onClick={handleSubmit(onSubmit)}
                variant='contained'
                sx={{ height: '35px', width: '80px' }}
                loading={isFetching}
              >
                Post
              </LoadingButton>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <FoGuestListTable
        isOpen={isGuestOpen}
        onClose={handleCloseGuestList}
        onAccount={(profileId, profileName, _, profileTIN) => {
          setProfileId(profileId);
          setProfileName(profileName);
          setProfileNameTin(profileTIN);
          setValue('payor_name', profileName);
          setValue('payor_tin', profileTIN);
        }}
        transactionType='Tax Credit'
        totalPayables={0}
      />
    </FormProvider>
  );
};

export default FORoomPostingTaxCreditForm;
